import React, { useEffect, useState, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";

interface DisputeOrder {
  _id: string;
  searchQuery: string;
  amount: number;
  status: string;
  customerEmail?: string;
}

interface DisputeDj {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  slug?: string;
}

interface Dispute {
  _id: string;
  stripeDisputeId: string;
  stripeChargeId?: string;
  order?: DisputeOrder | null;
  dj?: DisputeDj | null;
  amount: number;
  currency: string;
  reason?: string;
  status: string;
  evidenceDueBy?: string | null;
  customerEmail?: string;
  customerName?: string;
  clientNickname?: string;
  clientInstagram?: string;
  cardLast4?: string;
  createdAt: string;
}

function fmtAvatar(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

function formatReason(reason?: string): string {
  if (!reason) return "—";
  return reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const STATUS_BADGES: Record<string, { className: string; label: string }> = {
  needs_response: { className: "badge-declined", label: "Needs Response" },
  warning_needs_response: { className: "badge-declined", label: "Needs Response" },
  under_review: { className: "badge-queued", label: "Under Review" },
  warning_under_review: { className: "badge-queued", label: "Under Review" },
  won: { className: "badge-accepted", label: "Won" },
  lost: { className: "badge-declined", label: "Lost" },
  warning_closed: { className: "badge-pending", label: "Warning Closed" },
};

export default function AdminDisputesTab() {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .get("/disputes")
      .then((r) => setDisputes(r.data))
      .catch(() => setDisputes([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...disputes];
    if (filterStatus !== "all") {
      list = list.filter((d) => d.status === filterStatus || (filterStatus === "needs_response" && d.status === "warning_needs_response"));
    }
    return list;
  }, [disputes, filterStatus]);

  const stats = useMemo(() => {
    const open = disputes.filter((d) => d.status === "needs_response" || d.status === "warning_needs_response").length;
    const underReview = disputes.filter((d) => d.status === "under_review" || d.status === "warning_under_review").length;
    const won = disputes.filter((d) => d.status === "won").length;
    const lost = disputes.filter((d) => d.status === "lost").length;
    const totalAmount = disputes.reduce((acc, d) => acc + (d.amount || 0), 0);
    return { open, underReview, won, lost, total: disputes.length, totalAmount };
  }, [disputes]);

  if (loading) {
    return (
      <section className="fade-in">
        <p className="muted-text" style={{ textAlign: "center", padding: 40 }}>Loading disputes...</p>
      </section>
    );
  }

  return (
    <section className="fade-in">
      {/* Stats */}
      <div className="adm-grid-5" style={{ marginBottom: 14 }}>
        {[
          { label: "Total Disputes", value: String(stats.total), color: "" },
          { label: "Needs Response", value: String(stats.open), color: "var(--adm-red)" },
          { label: "Under Review", value: String(stats.underReview), color: "var(--adm-orange)" },
          { label: "Won", value: String(stats.won), color: "var(--adm-green)" },
          { label: "Lost", value: String(stats.lost), color: "var(--adm-red)" },
        ].map((s) => (
          <div key={s.label} className="kpi-card kpi-card-compact">
            <div>
              <div className="kpi-label">{s.label}</div>
              <div className="kpi-value" style={s.color ? { color: s.color } : {}}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter toolbar */}
      <div className="adm-toolbar" style={{ marginBottom: 14 }}>
        <div className="adm-toolbar-group">
          <select className="adm-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="needs_response">Needs Response</option>
            <option value="under_review">Under Review</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        <span className="muted-text" style={{ fontSize: "0.76rem" }}>
          {filtered.length} dispute{filtered.length !== 1 ? "s" : ""}
          {stats.totalAmount > 0 ? ` · $${(stats.totalAmount / 100).toFixed(2)} total disputed` : ""}
        </span>
      </div>

      {/* Table */}
      <div className="adm-card">
        {disputes.length === 0 ? (
          <p className="muted-text" style={{ textAlign: "center", padding: 30 }}>
            No disputes found. This is a good thing!
          </p>
        ) : (
          <div className="adm-scrollable-full" style={{ maxHeight: "calc(100vh - 320px)" }}>
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Reason</th>
                  <th>Talent</th>
                  <th>Client</th>
                  <th>Evidence Due</th>
                  <th>Order</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const badge = STATUS_BADGES[d.status] || { className: "badge-pending", label: d.status };
                  const dj = d.dj;
                  const avatarSrc = fmtAvatar(dj?.avatarUrl);
                  const isDue = d.evidenceDueBy && new Date(d.evidenceDueBy) > new Date();
                  const isOverdue = d.evidenceDueBy && new Date(d.evidenceDueBy) <= new Date();

                  const isExp = expandedId === d._id;
                  return (
                    <Fragment key={d._id}>
                    <tr className="hover-row">
                      <td style={{ whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                        {new Date(d.createdAt).toLocaleDateString()}{" "}
                        <span className="muted-text">{new Date(d.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td>
                        <span className={`badge badge-sm ${badge.className}`}>{badge.label}</span>
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        ${(d.amount / 100).toFixed(2)} {d.currency.toUpperCase()}
                      </td>
                      <td style={{ fontSize: "0.78rem" }}>{formatReason(d.reason)}</td>
                      <td>
                        {dj ? (
                          <div className="dj-cell dj-cell-compact clickable" onClick={() => navigate(`/admin/dj/${dj._id}`)}>
                            {avatarSrc ? (
                              <img src={avatarSrc} className="dj-cell-avatar-sm" alt="" />
                            ) : (
                              <div className="dj-cell-placeholder-sm">{dj.name?.[0]?.toUpperCase() || "?"}</div>
                            )}
                            <span className="dj-link-text" style={{ fontSize: "0.82rem" }}>{dj.name}</span>
                          </div>
                        ) : (
                          <span className="muted-text">Unknown</span>
                        )}
                      </td>
                      <td
                        style={{ fontSize: "0.78rem", cursor: "pointer" }}
                        onClick={() => setExpandedId(isExp ? null : d._id)}
                      >
                        {d.customerName || (d.clientInstagram ? `@${d.clientInstagram}` : null) || d.customerEmail || d.clientNickname || (d.cardLast4 ? `•••• ${d.cardLast4}` : null) || "—"}
                        <span className="muted-text" style={{ marginLeft: 4, fontSize: "0.65rem" }}>{isExp ? "▲" : "▼"}</span>
                      </td>
                      <td style={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                        {d.evidenceDueBy ? (
                          <span style={{ color: isOverdue ? "var(--adm-red)" : isDue ? "var(--adm-orange)" : undefined, fontWeight: isOverdue ? 700 : undefined }}>
                            {new Date(d.evidenceDueBy).toLocaleDateString()}
                            {isOverdue && " (overdue)"}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ fontSize: "0.76rem" }}>
                        {d.order ? (
                          <span className="muted-text" title={d.order.searchQuery}>
                            {d.order.searchQuery?.slice(0, 20) || d.order._id.slice(-6)}
                          </span>
                        ) : (
                          <span className="muted-text">—</span>
                        )}
                      </td>
                    </tr>
                    {isExp && (
                      <tr>
                        <td colSpan={8} style={{ padding: 0, background: "#f9fafb" }}>
                          <div style={{ display: "flex", gap: 24, padding: "10px 14px", fontSize: "0.78rem" }}>
                            {[
                              { label: "Real Name", value: d.customerName },
                              { label: "Instagram", value: d.clientInstagram ? `@${d.clientInstagram}` : null },
                              { label: "Email", value: d.customerEmail },
                              { label: "Nickname", value: d.clientNickname },
                              { label: "Card", value: d.cardLast4 ? `•••• ${d.cardLast4}` : null },
                            ].map((item) => (
                              <div key={item.label}>
                                <div className="muted-text" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
                                <div style={{ fontWeight: 600 }}>{item.value || "—"}</div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
